import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// ============================================================
// SignRouteMap — the day's route drawn on a real map.
// Shop (gold square) → numbered stops in drive order → dashed
// return leg home. Re-renders live as the manager reorders,
// applies a Build Day proposal, or switches dates.
// ============================================================

// OpenStreetMap's own tiles — free, no API key. (CARTO's basemaps started
// requiring a key and watermarked every tile "API KEY REQUIRED".)
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Sign tasks (supply runs etc.) pin in teal so they never read as a job.
function numberIcon(n, emergency, done, task) {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:30px;height:30px;border-radius:50%;
      background:${done ? '#059669' : task ? '#0d9488' : emergency ? '#dc2626' : '#0b1121'};color:#fff;
      display:flex;align-items:center;justify-content:center;
      font-weight:800;font-size:13px;font-family:inherit;
      border:2.5px solid #fff;box-shadow:0 2px 6px rgba(15,23,42,0.4);
    ">${done ? '✓' : n}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

const shopIcon = L.divIcon({
  className: '',
  html: `<div style="
    width:30px;height:30px;border-radius:8px;
    background:linear-gradient(135deg,#d97706,#f59e0b);color:#fff;
    display:flex;align-items:center;justify-content:center;
    font-size:15px;border:2.5px solid #fff;box-shadow:0 2px 6px rgba(15,23,42,0.4);
  ">🏠</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

export default function SignRouteMap({ shop, stops, height = 340 }) {
  const containerRef = useRef(null);
  const [unmapped, setUnmapped] = useState([]);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: true, scrollWheelZoom: false, attributionControl: true,
    });
    L.tileLayer(TILE_URL, { attribution: TILE_ATTR, maxZoom: 19 }).addTo(map);
    // Mute OSM's full-color style so the route and pins stay the loudest thing.
    // Filter the tile PANE (exists from construction) — the layer's own
    // container isn't built until the map has a view.
    map.getPane('tilePane').style.filter = 'saturate(0.35) brightness(1.04)';
    map.setView([32.78, -97.1], 10);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layerRef.current) { layerRef.current.remove(); layerRef.current = null; }

    // Number FIRST, filter second. Getting this backwards is what made the
    // manager's map disagree with the list: a stop with no coordinates was
    // dropped and everything after it shifted up a number, so map pin 1 was
    // really stop 2. The number a stop carries is its position in the day,
    // whether or not we can draw it.
    const numbered = (stops || []).map((s, i) => ({ ...s, _n: s.stop_number ?? i + 1 }));
    const pinned = numbered.filter(s => s.lat != null && s.lng != null);
    // A route map that silently hides stops is worse than no map — the manager
    // plans the drive off it. Surface what isn't drawn.
    setUnmapped(numbered.filter(s => s.lat == null || s.lng == null));
    const group = L.featureGroup();

    if (shop?.lat != null) {
      L.marker([shop.lat, shop.lng], { icon: shopIcon, zIndexOffset: 500 })
        .bindTooltip('Shop — start & end', { direction: 'top', offset: [0, -14] })
        .addTo(group);
    }

    const path = [];
    if (shop?.lat != null) path.push([shop.lat, shop.lng]);
    pinned.forEach((s, i) => {
      const n = s._n;
      const done = s.status === 'completed' || s.status === 'done';
      path.push([s.lat, s.lng]);
      L.marker([s.lat, s.lng], { icon: numberIcon(n, !!s.is_emergency, done, !!s.is_task), zIndexOffset: 1000 - i })
        .bindTooltip(
          `<strong>${n}. ${s.account_name || `#${s.id}`}${done ? ' — completed' : ''}</strong><br>${s.work_address || ''}`,
          { direction: 'top', offset: [0, -14] },
        )
        .addTo(group);
    });

    if (path.length >= 2) {
      L.polyline(path, { color: '#0b1121', weight: 3, opacity: 0.75 }).addTo(group);
      if (shop?.lat != null && pinned.length > 0) {
        // Dashed return leg — last stop back home
        const last = pinned[pinned.length - 1];
        L.polyline([[last.lat, last.lng], [shop.lat, shop.lng]], {
          color: '#64748b', weight: 2.5, opacity: 0.7, dashArray: '6 8',
        }).addTo(group);
      }
    }

    group.addTo(map);
    layerRef.current = group;
    if (group.getLayers().length > 0) {
      map.fitBounds(group.getBounds().pad(0.18), { maxZoom: 13 });
    }
  }, [shop, stops]);

  return (
    <div>
      <div ref={containerRef} style={{
        height, borderRadius: 12, overflow: 'hidden', zIndex: 0,
        border: '1px solid var(--border-light, #e2e8f0)',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
      }} />
      {unmapped.length > 0 && (
        <div style={{
          marginTop: 6, fontSize: 11.5, lineHeight: 1.5,
          padding: '7px 10px', borderRadius: 8,
          background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
        }}>
          <strong>
            {unmapped.length} stop{unmapped.length === 1 ? '' : 's'} not on the map
          </strong>{' '}
          — no coordinates yet:{' '}
          {unmapped.map(s => `${s._n}. ${s.account_name || `#${s.id}`}`).join(' · ')}.
          {' '}They're still on the route. Pin them in Sign Setup so the drive time is right.
        </div>
      )}
    </div>
  );
}
